import React from 'react';
import { Card, CardContent } from '@mui/material';
import { DateInput, TextInput, CountryDropdown, ClearAfterUnmountWrapper, POLAND } from 'framework/form';
import { DOCUMENTS_ALIAS } from 'domain/shared/employee';
import PassportNumberInput from 'domain/people/employee/shared/input/PassportNumberInput';
import DocumentsDropdown from '../DocumentsDropdown';
import { DocumentScanUploaderInput } from 'domain/shared/employee';

type DocumentDetails = {
  series?: string;
  personalIdNumber?: string;
  citizenship?: string;
  issuedBy?: string;
  issuedAt?: string;
  givenName?: string;
  surname?: string;
  countryOfRegistration?: string;
};

type DocumentValue = {
  type?: string;
  number?: string;
  documentDetails?: DocumentDetails;
  expiredAt?: string;
  scansIds?: string[];
};

type CardDocumentInputProps = {
  source: string;
  value: DocumentValue;
  onRemove?: () => void;
  showRemove?: boolean;
  onChange?: (value: DocumentValue) => void;
};

type DocumentTypeConfig = {
  hasScans: boolean;
  renderNumberInput: (source: string) => React.ReactElement;
  fields: FieldConfig[];
};

type FieldConfig<TProps = any> = {
  component: React.ComponentType<TProps>;
  source: string;
  label: string;
  props?: Partial<TProps>;
};

const DOCUMENT_FIELDS_BY_TYPE: Record<string, DocumentTypeConfig> = {
  [DOCUMENTS_ALIAS.PESEL]: {
    hasScans: false,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber />,
    fields: [{ component: CountryDropdown, source: 'documentDetails.citizenship', label: 'Citizenship' }],
  },

  [DOCUMENTS_ALIAS.NIP]: {
    hasScans: false,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber />,
    fields: [
      {
        component: CountryDropdown,
        source: 'documentDetails.countryOfRegistration',
        label: 'Country of registration',
        props: {
          priorityCountries: [POLAND.name],
        },
      },
    ],
  },

  [DOCUMENTS_ALIAS.REGON]: {
    hasScans: false,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber />,
    fields: [],
  },

  DEFAULT: {
    hasScans: true,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber={false} />,
    fields: [
      { component: TextInput, source: 'documentDetails.personalIdNumber', label: 'Identification number' },
      { component: CountryDropdown, source: 'documentDetails.citizenship', label: 'Citizenship' },
      { component: DateInput, source: 'documentDetails.issuedAt', label: 'Date of issue' },
      { component: DateInput, source: 'expiredAt', label: 'Expiry date' },
      { component: TextInput, source: 'documentDetails.issuedBy', label: 'Issued by' },
      { component: TextInput, source: 'documentDetails.surname', label: 'Surname' },
      { component: TextInput, source: 'documentDetails.givenName', label: 'Given name' },
    ],
  },
};

const CardDocumentInput: React.FC<CardDocumentInputProps> = ({
  source,
  value,
  onRemove,
  showRemove = false,
  onChange,
}) => {
  const type = value?.type as any;

  const config = DOCUMENT_FIELDS_BY_TYPE[type] || DOCUMENT_FIELDS_BY_TYPE.DEFAULT;

  if (!type) {
    return (
      <Card sx={{ overflow: 'visible' }}>
        <CardContent className="card-document-content">
          <DocumentsDropdown source={`${source}.type`} label="Document type" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent className="card-document-content">
        <DocumentsDropdown source={`${source}.type`} label="Document type" />
        <div className="document-grid">
          {config.renderNumberInput(source)}

          {config.fields.map((field, idx) => {
            const Component = field.component;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <ClearAfterUnmountWrapper key={idx}>
                <Component source={`${source}.${field.source}`} label={field.label} {...(field.props || {})} />
              </ClearAfterUnmountWrapper>
            );
          })}
        </div>

        {config.hasScans && (
          <ClearAfterUnmountWrapper>
            <div className="document-scans">
              <DocumentScanUploaderInput label="Document scans" source={`${source}.scansIds`} />
            </div>
          </ClearAfterUnmountWrapper>
        )}
      </CardContent>
    </Card>
  );
};

export default CardDocumentInput;
